<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('projectList.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          { text: $t('projectList.pageTitle'), active: true }
        ]"
      />
    </div>
    <ts-loading-banner :loading="loading">
      <ts-card>
        <ts-card-wrapper>
          <div class="row">
            <div class="col-md-3">
              <label>{{ $t('projectList.status') }}</label>
              <a-select
                v-model="model.status_id"
                :options="status"
                showSearch
                :filterOption="filterOption"
                placeholder="--select--"
                mode="multiple"
                class="ts-w-full"
                :max-tag-count="1"
                :maxTagTextLength="30"
              />
              <div class="validate" v-if="errors.has('status_id')">
                {{ errors.first('status_id') }}
              </div>
            </div>
            <div class="col-md-3">
              <label>{{ $t('projectList.owner') }}</label>
              <a-select
                v-model="model.employee_id"
                :options="owners"
                showSearch
                :filterOption="filterOption"
                placeholder="--select--"
                mode="multiple"
                class="ts-w-full"
                :max-tag-count="1"
                :maxTagTextLength="30"
              />
            </div>
            <div class="col-md-3">
              <label>{{ $t('projectList.customer') }}</label>
              <a-select
                v-model="model.customer_id"
                :options="customers"
                showSearch
                :filterOption="filterOption"
                placeholder="--select--"
                mode="multiple"
                class="ts-w-full"
                :max-tag-count="1"
                :maxTagTextLength="30"
              />
            </div>
          </div>
          <div class="row ts-mt-2">
            <div class="col-md-9 ts-flex ts-justify-end ts-space-x-2 ts-h-8">
              <ts-button @click="onPreview" color="primary">
                {{ $t('view') }}
              </ts-button>
              <ts-button @click="exportExcel" color="info">
                {{ $t('excel') }}
              </ts-button>
              <ts-button @click="previewPdf" color="info">
                {{ $t('preview') }}
              </ts-button>
            </div>
          </div>
        </ts-card-wrapper>
      </ts-card>
      <ts-card>
        <ts-card-wrapper>
          <ts-table :columns="columns" :records="resources">
            <template v-slot="{ record, index }">
              <td class="white-space-nowrap">{{ index + 1 }}</td>
              <td class="white-space-nowrap">{{ record.project_name }}</td>
              <td class="white-space-nowrap">{{ record.project_status }}</td>
              <td class="white-space-nowrap">{{ record.label }}</td>
              <td class="white-space-nowrap">{{ record.request_name }}</td>
              <td class="white-space-nowrap">{{ record.customer_name }}</td>
              <td class="white-space-nowrap">
                {{ record.completion_percentage }}%
              </td>
              <td class="white-space-nowrap">
                {{ record.created_by }}
              </td>
              <td class="white-space-nowrap">
                {{ record.created_date || '-' }}
              </td>
              <td class="text-center">
                <a
                  href="#"
                  class="text-primary"
                  @click.prevent="onEdit(record)"
                  v-tooltip="$t('edit')"
                >
                  <Icon type="ios-create" size="20" />
                </a>
              </td>
            </template>
          </ts-table>
        </ts-card-wrapper>
      </ts-card>
      <a-modal
        v-if="showUpdateForm"
        v-model="showUpdateForm"
        :title="$t('projectList.pageTitle')"
        :footer="null"
        :centered="true"
        :maskClosable="false"
        :zIndex="1000"
        width="20%"
        @cancel="onCancelUpdate"
      >
        <UpdateForm
          @cancel="onCancelUpdate"
          @updated="onUpdateSuccess"
          :updateData="updateData"
        />
      </a-modal>
    </ts-loading-banner>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapActions } from 'vuex'
import { trim } from 'lodash'
import * as FileDownload from 'downloadjs'
import PdfPreview from './components/preview-pdf.vue'
import UpdateForm from './components/update-form.vue'

export default {
  components: { UpdateForm },
  data() {
    return {
      errors: new Errors(),
      loading: false,
      resources: [],
      owners: [],
      status: [],
      customers: [],
      updateData: null,
      showUpdateForm: false,
      model: {
        status_id: [],
        employee_id: [],
        customer_id: []
      }
    }
  },
  mounted() {
    this.fetchData()
  },
  computed: {
    columns() {
      return [
        { name: this.$t('projectList.numbering') },
        { name: this.$t('projectList.projectName') },
        { name: this.$t('projectList.projectStatus') },
        { name: this.$t('projectList.priority') },
        { name: this.$t('projectList.owner') },
        { name: this.$t('projectList.customerName') },
        { name: this.$t('projectList.completionPercentage') },
        { name: this.$t('projectList.createdBy') },
        { name: this.$t('projectList.createdDate') },
        { name: this.$t('projectList.action'), slot: 'action', fixed: 'right' }
      ]
    }
  },
  methods: {
    ...mapActions('report/project', [
      'getOwner',
      'getCustomer',
      'getStatus',
      'projectList',
      'projectListExcel',
      'getRequesters'
    ]),
    async fetchData() {
      this.loading = true
      const status = await this.getStatus()
      this.status = status.map(el => ({
        value: el.status_id,
        label: el.project_status
      }))
      const customers = await this.getCustomer()
      this.customers = customers.map(el => ({
        value: el.customer_id,
        label: el.customer_name
      }))
      const owners = await this.getOwner()
      this.owners = owners.map(el => ({
        value: el.employee_id,
        label: el.employee_name_en
      }))
      this.loading = false
    },
    onEdit(record) {
      this.showUpdateForm = true
      this.updateData = {
        project_id: record.project_id, // for child
        project_status: record.project_status || '',
        status_id: record.status_id || ''
      }
    },
    onCancelUpdate() {
      this.showUpdateForm = false
      this.updateData = null
    },
    onUpdateSuccess() {
      this.showUpdateForm = false
      this.updateData = null
      this.onPreview()
    },
    onPreview() {
      this.loading = true
      this.projectList(this.model)
        .then(result => {
          this.resources = result.data
        })
        .catch(err => {
          this.$toasted.error(err.message)
          this.errors = new Errors(err.errors)
        })
        .finally(() => (this.loading = false))
    },
    exportExcel() {
      this.loading = true
      this.errors = new Errors()
      this.projectListExcel(this.model)
        .then(response => {
          const filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          const error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.loading = false))
    },
    filterOption(input, option) {
      return option.componentOptions.children[0].text
        .toLowerCase()
        .includes(input.toLowerCase())
    },
    previewPdf() {
      this.$modal.show(
        PdfPreview,
        {
          status_id: this.model.status_id || [],
          employee_id: this.model.employee_id || [],
          customer_id: this.model.customer_id || []
        },
        {
          height: 'auto',
          width: '70%',
          scrollable: true,
          draggable: '.form-header',
          clickToClose: false
        }
      )
    }
  }
}
</script>

<style scoped>
.white-space-nowrap {
  white-space: nowrap;
}
</style>
